<template>
  <div class="home">
    <!-- Mensagem de erro (Produção sem QueryString) -->
    <div v-if="errorMessage" class="error-container">
      <div class="error-icon">⚠️</div>
      <h2>Não foi possível continuar</h2>
      <p>{{ errorMessage }}</p>
    </div>

    <!-- Formulário de configuração (apenas em Dev sem QueryString) -->
    <div v-if="showForm && !showDiscador" class="config-form">
      <div class="dev-badge">Modo Desenvolvimento</div>
      
      <div class="dev-info">
        <p>Este formulário está disponível apenas em ambiente de desenvolvimento para facilitar o acesso e realizar testes sem necessidade de QueryString.</p>
        <p>Em produção, o acesso é exclusivo via QueryString com os parâmetros obrigatórios.</p>
      </div>
      
      <div class="form-group">
        <label for="primaryUID">Primário UID (11 números):</label>
        <input 
          id="primaryUID"
          v-model="config.primaryUID" 
          type="text" 
          pattern="[0-9]*"
          placeholder="Ex: 12345678901"
          maxlength="11"
          @input="handleUIDInput($event, 'primaryUID')"
        />
        <small v-if="uidErrors.primaryUID" class="error-text">{{ uidErrors.primaryUID }}</small>
      </div>

      <div class="form-group">
        <label for="secondaryUID">Secundário UID (11 números):</label>
        <input 
          id="secondaryUID"
          v-model="config.secondaryUID" 
          type="text" 
          pattern="[0-9]*"
          placeholder="Ex: 98765432109"
          maxlength="11"
          @input="handleUIDInput($event, 'secondaryUID')"
        />
        <small v-if="uidErrors.secondaryUID" class="error-text">{{ uidErrors.secondaryUID }}</small>
      </div>

      <div class="form-group">
        <label for="client">Client (código do cliente, até 15 caracteres):</label>
        <input 
          id="client"
          v-model="config.client" 
          type="text" 
          placeholder="Ex: pleni"
          maxlength="15"
        />
      </div>

      <button @click="startDiscador" class="btn-start">
        Iniciar Discador
      </button>
    </div>

    <!-- Componente Discador -->
    <Discador 
      v-if="showDiscador"
      ref="discador"
      :systemCode="config.systemCode"
      :systemPassword="config.systemPassword"
      :primaryUID="config.primaryUID"
      :secondaryUID="config.secondaryUID"
      :client="config.client"
      :skipPreCall="config.skipPreCall"
      :awaitSecondary="config.awaitSecondary"
      :primaryName="config.primaryName"
      :secondaryName="config.secondaryName"
      @leaveRoom="handleLeaveRoom"
      @callFinished="handleCallFinished"
      @alreadyInRoom="handleAlreadyInRoom"
    />
  </div>
</template>

<script>
import Discador from '@a4pm/discador'
import './WebviewHome.css'

const DISPLAY_NAME_MAX_LENGTH = 80

function toDisplayName(value) {
  if (value == null) {
    return ''
  }

  return String(value).replace(/\s+/g, ' ').trim().slice(0, DISPLAY_NAME_MAX_LENGTH)
}

/**
 * Componente principal da aplicação de Discador
 * Suporta dois modos de operação:
 * - Desenvolvimento: Permite acesso via formulário sem QueryString
 * - Produção: Exige parâmetros obrigatórios via QueryString
 * 
 * @component
 * @example
 * // Quem liga (primary) aguarda o secondary
 * ?primary=01234567890&secondary=98765432100&client=pleni&skipPreCall=true&awaitSecondary=true
 * // Quem atende: mesmo par invertido, mesmo client, sem awaitSecondary
 * // Nomes (opcional, JWT ou query): primaryName=quem abre, secondaryName=o outro
 * 
 * // Desenvolvimento
 * Acesso direto sem QueryString exibe formulário
 */
export default {
  name: 'HomeView',
  components: {
    Discador
  },
  data() {
    return {
      /** @type {boolean} Controla exibição do componente Discador */
      showDiscador: false,
      /** @type {boolean} Controla exibição do formulário de desenvolvimento */
      showForm: false,
      /** @type {boolean} Indica se está em modo desenvolvimento */
      isDevMode: false,
      /** @type {boolean} Indica se QueryString foi detectada */
      hasQueryString: false,
      /** @type {string} Mensagem de erro para acesso negado */
      errorMessage: '',
      /** @type {Object} Mensagens de erro para validação de UIDs */
      uidErrors: {
        primaryUID: '',
        secondaryUID: ''
      },
      /** @type {Object} Configuração do sistema e usuários */
      config: {
        /** @type {string|null} Código do sistema */
        systemCode: process.env.VUE_APP_SYSTEM_CODE || null,
        /** @type {string|null} Senha do sistema */
        systemPassword: process.env.VUE_APP_SYSTEM_PASSWORD || null,
        /** @type {string|number|null} UID do usuário primário (alfanumérico ou numérico) */
        primaryUID: null,
        /** @type {string|number|null} UID do usuário secundário (alfanumérico ou numérico) */
        secondaryUID: null,
        /** @type {string|null} Código do cliente (cli_codigo / csa_codigo_cliente) */
        client: null,
        /** @type {boolean} Pula pré-chamada e entra direto na sala */
        skipPreCall: false,
        /** @type {boolean} Primary aguarda o secondary entrar na sala */
        awaitSecondary: false,
        /** @type {string} Nome do primaryUID */
        primaryName: '',
        /** @type {string} Nome do secondaryUID */
        secondaryName: '',
      }
    }
  },

  created() {
    void this.initializeApp();
  },
  mounted() {
    window.addEventListener('message', this.handleDiscadorPostMessage)
  },
  beforeDestroy() {
    window.removeEventListener('message', this.handleDiscadorPostMessage)
    console.log('@A4 [WEBVIEW] BEFORE DESTROY')
  },
  
  watch: {
    '$route.query.action'(newValue) {
      console.log('@A4 => R$route.query.action', newValue);
      if (newValue === 'end_call') {
        console.log('@A4 => Recebi o comando para encerrar a chamada');
        this.handleCloseDiscadorIframe()
      }
    }
  },  

  methods: {
    handleDiscadorPostMessage(message) {
      const data =
          typeof message.data === 'string'
            ? JSON.parse(message.data)
            : message.data
      const type = data.type || data.eventName
      if (type) {
        switch (type) {
          case 'leaveRoom':
          case 'END_CALL':
          case 'FINISH_CALL':
          case 'CANCEL':
            return this.handleCloseDiscadorIframe()
          default:
            console.log('@A4 [handleDiscadorPostMessage]', {
              data,
              originalMessage: message
            })
            break
        }
      }

    },
    handleCloseDiscadorIframe() {
      console.log('@A4 [WebviewHome] handleCloseDiscadorIframe')
      console.log('@A4 [WebviewHome] handleCloseDiscadorIframe $ref', this.$refs)
      console.log('@A4 [WebviewHome] handleCloseDiscadorIframe $ref metod', this.$refs?.discador?.handleCloseDiscadorIframe)
      this.$refs?.discador?.handleCloseDiscadorIframe()
    },
    /**
     * Valida e formata o UID, permitindo apenas 0-9 e limitando a 11 caracteres
     * @param {Event} event - Evento de input
     * @param {string} field - Nome do campo a ser atualizado
     * @returns {void}
     */
    handleUIDInput(event, field) {
      let value = event.target.value;
      
      // Remove caracteres não permitidos (apenas 0-9)
      value = value.replace(/[^0-9]/g, '');
      
      // Limita a 11 caracteres
      if (value.length > 11) {
        value = value.slice(0, 11);
      }
      
      // Atualiza o valor
      this.config[field] = value;
      
      // Valida o UID em tempo real
      this.validateUID(value, field);
    },

    /**
     * Valida um UID individual
     * @param {string} uid - UID a ser validado
     * @param {string} field - Nome do campo (primaryUID ou secondaryUID)
     * @returns {boolean} true se válido, false caso contrário
     */
    validateUID(uid, field) {
      // Limpa erro anterior
      this.uidErrors[field] = '';
      
      if (!uid) {
        return false;
      }
      
      // Verifica se tem exatamente 11 caracteres
      if (uid.length !== 11) {
        if (uid.length > 0) {
          this.uidErrors[field] = 'UID deve ter exatamente 11 números';
        }
        return false;
      }
      
      // Verifica se contém apenas 0-9
      if (!/^[0-9]{11}$/.test(uid)) {
        this.uidErrors[field] = 'UID deve conter apenas números (0-9)';
        return false;
      }
      
      return true;
    },

    /**
     * Inicializa a aplicação detectando o ambiente e QueryString
     * - Com QueryString: força modo Produção e auto-inicia
     * - Sem QueryString em Dev: exibe formulário
     * - Sem QueryString em Prod: bloqueia acesso
     * @returns {void}
     */
    async initializeApp() {
      const isDevelopment = process.env.VUE_APP_ENVIRONMENT === 'development';
      const queryParams = this.getQueryParams();
      const hasQueryString = Object.keys(queryParams).length > 0;

      this.hasQueryString = hasQueryString;

      if (hasQueryString && queryParams.t) {
        this.isDevMode = false;
        await this.loadFromToken(String(queryParams.t).trim());
        return;
      }

      // Se houver QueryString, sempre força modo Produção
      if (hasQueryString) {
        this.isDevMode = false;
        this.loadFromQueryString(queryParams);
      } 
      // Sem QueryString em desenvolvimento - permite formulário
      else if (isDevelopment) {
        this.isDevMode = true;
        this.showForm = true;
      } 
      // Sem QueryString em produção - bloqueia acesso
      else {
        this.errorMessage = 'Não foi possível iniciar a chamada. Tente novamente mais tarde.';
      }
    },

    /**
     * Extrai parâmetros da QueryString (search, rota Vue e hash).
     * @returns {Object.<string, string>}
     */
    getQueryParams() {
      const params = {};

      const appendParams = (searchParams) => {
        for (const [key, value] of searchParams.entries()) {
          if (!Object.prototype.hasOwnProperty.call(params, key)) {
            params[key] = value;
          }
        }
      };

      appendParams(new URLSearchParams(window.location.search));

      const routeQuery = this.$route && this.$route.query;
      if (routeQuery) {
        Object.keys(routeQuery).forEach((key) => {
          const value = routeQuery[key];
          if (value !== null && value !== '' && !Object.prototype.hasOwnProperty.call(params, key)) {
            params[key] = Array.isArray(value) ? String(value[0]) : String(value);
          }
        });
      }

      const hash = window.location.hash || '';
      const hashQueryIndex = hash.indexOf('?');
      if (hashQueryIndex !== -1) {
        appendParams(new URLSearchParams(hash.slice(hashQueryIndex + 1)));
      }

      return params;
    },

    /**
     * Valida config obrigatória e retorna mensagem específica quando inválida.
     * @returns {{ valid: boolean, message?: string }}
     */
    getConfigValidationError() {
      const { systemCode, systemPassword, primaryUID, secondaryUID, client } = this.config;
      const defaultErrorMessage = {
        valid: false,
        message: 'Não foi possível iniciar a chamada. Tente novamente mais tarde.',
      };

      if (!systemCode || !systemPassword) {
        return defaultErrorMessage;
      }

      if (!primaryUID || !secondaryUID) {
        return defaultErrorMessage;
      }

      if (!client || String(client).trim().length === 0 || String(client).trim().length > 15) {
        return defaultErrorMessage;
      }

      const primaryUIDStr = String(primaryUID);
      const secondaryUIDStr = String(secondaryUID);

      if (primaryUIDStr.length !== 11 || secondaryUIDStr.length !== 11) {
        return defaultErrorMessage;
      }

      if (!/^[0-9]{11}$/.test(primaryUIDStr) || !/^[0-9]{11}$/.test(secondaryUIDStr)) {
        return defaultErrorMessage;
      }

      return { valid: true };
    },

    /**
     * Resolve JWT ?t= via discador-api e monta a configuração local.
     * @param {string} token
     * @returns {Promise<void>}
     */
    async loadFromToken(token) {
      const defaultErrorMessage = 'Não foi possível iniciar a chamada. Tente novamente mais tarde.';
      const apiUrl = window.__DISCADOR_API_URL__;
      const resolveBase = apiUrl || '';

      if (!token) {
        this.errorMessage = defaultErrorMessage;
        return;
      }

      if (!resolveBase && process.env.VUE_APP_ENVIRONMENT !== 'development') {
        this.errorMessage = defaultErrorMessage;
        return;
      }

      try {
        const response = await fetch(`${resolveBase}/call-enter/resolve?t=${encodeURIComponent(token)}`);

        if (!response.ok) {
          this.errorMessage = defaultErrorMessage;
          return;
        }

        const data = await response.json();
        this.config.primaryUID = data.primary;
        this.config.secondaryUID = data.secondary;
        this.config.client = String(data.client || '').trim().slice(0, 15);
        this.config.skipPreCall = data.skipPreCall !== false;
        this.config.awaitSecondary = data.awaitSecondary === true;
        this.config.primaryName = toDisplayName(data.primaryName);
        this.config.secondaryName = toDisplayName(data.secondaryName);

        const validation = this.getConfigValidationError();
        if (!validation.valid) {
          this.errorMessage = validation.message || defaultErrorMessage;
          return;
        }

        this.showDiscador = true;
      } catch {
        this.errorMessage = defaultErrorMessage;
      }
    },

    /**
     * Carrega configurações a partir dos parâmetros da QueryString
     * Valida e auto-inicia o discador se configuração válida
     * @param {Object.<string, string>} params - Parâmetros extraídos da QueryString
     * @returns {void}
     */
    loadFromQueryString(params) {  
      if (params.primary) {
        const digits = String(params.primary).replace(/\D/g, '');
        this.config.primaryUID = digits.padStart(11, '0').slice(-11);
      }
      const secondaryRaw = params.secondary || params.referencia;
      if (secondaryRaw) {
        const digits = String(secondaryRaw).replace(/\D/g, '');
        this.config.secondaryUID = digits.padStart(11, '0').slice(-11);
      }

      if (params.client) {
        this.config.client = String(params.client).trim().slice(0, 15);
      }

      const skipRaw = String(params.skipPreCall || '').toLowerCase();
      this.config.skipPreCall = skipRaw === '1' || skipRaw === 'true';

      const awaitRaw = String(params.awaitSecondary || '').toLowerCase();
      this.config.awaitSecondary = awaitRaw === '1' || awaitRaw === 'true';
      this.config.primaryName = toDisplayName(params.primaryName);
      this.config.secondaryName = toDisplayName(params.secondaryName);

      // Validar configuração
      const validation = this.getConfigValidationError();
      if (!validation.valid) {
        this.errorMessage = validation.message || 'Não foi possível iniciar a chamada. Tente novamente mais tarde.';
        return;
      }
      
      // Auto-iniciar discador
      this.showDiscador = true;
    },

    /**
     * Valida se todas as configurações obrigatórias estão presentes e válidas
     * UIDs devem conter exatamente 11 números (0-9)
     * @returns {boolean} true se configuração válida, false caso contrário
     */
    validateConfig() {
      return this.getConfigValidationError().valid;
    },

    /**
     * Inicia o discador após validação dos dados do formulário
     * Valida UIDs numéricos (11 dígitos) antes de iniciar
     * @returns {void}
     */
    startDiscador() {
      // Validação básica
      if (!this.config.systemCode || !this.config.systemPassword || !this.config.primaryUID || !this.config.secondaryUID) {
        alert('Por favor, preencha todos os campos!');
        return;
      }

      const client = this.config.client && String(this.config.client).trim().slice(0, 15);
      if (!client) {
        alert('Client é obrigatório (até 15 caracteres)!');
        return;
      }
      this.config.client = client;

      // Validar formato numérico
      const primaryUIDStr = String(this.config.primaryUID);
      const secondaryUIDStr = String(this.config.secondaryUID);
      
      // Validar comprimento
      if (primaryUIDStr.length !== 11) {
        alert('Primário UID deve ter exatamente 11 números!');
        return;
      }
      
      if (secondaryUIDStr.length !== 11) {
        alert('Secundário UID deve ter exatamente 11 números!');
        return;
      }
      
      if (!/^[0-9]{11}$/.test(primaryUIDStr)) {
        alert('Primário UID deve conter apenas números (0-9)!');
        return;
      }
      
      if (!/^[0-9]{11}$/.test(secondaryUIDStr)) {
        alert('Secundário UID deve conter apenas números (0-9)!');
        return;
      }

      // Atualizar config com valores normalizados
      this.config.primaryUID = primaryUIDStr;
      this.config.secondaryUID = secondaryUIDStr;
      
      this.showDiscador = true;
      this.showForm = false;
    },

    /**
     * Notifica o app nativo ao sair da sala.
     * @param {Object} data - Dados do evento
     * @returns {void}
     */
    handleLeaveRoom(data) {
      this.notifyNativeHost('leaveRoom', data);
    },

    /**
     * Notifica o app nativo ao finalizar a chamada.
     * @param {Object} data - Dados do evento
     * @returns {void}
     */
    handleCallFinished(data) {
      this.notifyNativeHost('callFinished', data);
    },

    /**
     * Notifica o host que o mesmo usuário já está na sala em outro dispositivo.
     * @param {Object} data - Dados do evento
     * @returns {void}
     */
    handleAlreadyInRoom(data) {
      this.notifyNativeHost('alreadyInRoom', data);
    },

    /**
     * Envia evento para o React Native WebView (meu-esus-app)
     * ou para o iframe parent (portal-teleconsulta / PEP).
     * @param {string} eventName
     * @param {Object} [data]
     */
    notifyNativeHost(eventName, data = {}) {
      const payload = JSON.stringify({
        type: eventName,
        eventName,
        data,
      });

      try {
        if (window.ReactNativeWebView?.postMessage) {
          window.ReactNativeWebView.postMessage(payload);
        }
      } catch (_error) {
        // host nativo ausente (browser / playground)
      }

      try {
        if (window.parent && window.parent !== window) {
          window.parent.postMessage(payload, '*');
        }
      } catch (_error) {
        // parent ausente
      }
    },
  }
}
</script>
